import React, { useEffect, useState } from 'react';
import { Dialog, DialogBackdrop, DialogPanel, Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react';
import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { GetAllPersona, PersonaProfileResponse } from '../../api/types';
import LoadingSpinner from '../../shared/spinner';

interface PreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  persona: GetAllPersona | null;
  onSelect: (profileId: string, persona: GetAllPersona) => void;
  isSelecting: boolean;
  profiles: PersonaProfileResponse[];
  defaultProfileId: string;
}

const PreviewModal: React.FC<PreviewModalProps> = ({
   isOpen,
   onClose,
   persona,
   onSelect,
   isSelecting,
   profiles,
 }) => {
  const [selectedProfileId, setSelectedProfileId] = useState<string>();

  useEffect(() => {
    const defaultProfile = profiles.find((p) => p.type === 'primary');
    if(defaultProfile){
      setSelectedProfileId(defaultProfile.id)
    }
  }, [profiles]);

  if (!persona) return null;

  const handleSelectClick = () => {
    if(selectedProfileId){
      onSelect(selectedProfileId, persona);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

      <div className="fixed inset-0 z-10 flex items-center justify-center p-4">
        <DialogPanel className="relative w-full max-w-4xl rounded-lg bg-white shadow-xl">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute right-2 top-2 z-10 rounded-full bg-white/80 p-1.5 text-gray-500 hover:text-gray-700"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>

          {/* Image viewer */}
          <div className="relative bg-black">
            <div className="relative w-full">
              <img
                src={persona.swappedFaceUrl ?? ''}
                alt="Preview your neoface"
                className="mx-auto max-h-[70vh] w-auto object-contain"
              />
            </div>
          </div>

          {/* Action buttons */}
          <div className="space-y-2 p-3">
            <div className="flex flex-col items-center gap-2">
                <>
                  {/* Profile selection dropdown */}
                    <div className="text-sm text-gray-700">
                      <span className="mr-2">Assign to:</span>
                      <Menu as="div" className="relative inline-block text-left">
                        <div>
                          <MenuButton className="inline-flex w-full justify-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
                            {profiles.find((p) => p.id === selectedProfileId)?.displayName ?? 'Select Profile'}
                            <ChevronDownIcon className="-mr-1 h-5 w-5 text-gray-400" aria-hidden="true" />
                          </MenuButton>
                        </div>

                        <Transition
                          enter="transition ease-out duration-100"
                          enterFrom="transform opacity-0 scale-95"
                          enterTo="transform opacity-100 scale-100"
                          leave="transition ease-in duration-75"
                          leaveFrom="transform opacity-100 scale-100"
                          leaveTo="transform opacity-0 scale-95"
                        >
                          <MenuItems className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none">
                            <div className="py-1">
                              {profiles.map((profile) => (
                                <MenuItem key={profile.id}>
                                  {({ active }) => (
                                    <button
                                      onClick={() => setSelectedProfileId(profile.id)}
                                      className={`${
                                        active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                                      } block w-full px-4 py-2 text-left text-sm`}
                                    >
                                      {profile.displayName}
                                    </button>
                                  )}
                                </MenuItem>
                              ))}
                            </div>
                          </MenuItems>
                        </Transition>
                      </Menu>
                    </div>

                  <button
                    onClick={handleSelectClick}
                    disabled={isSelecting || !selectedProfileId}
                    className="flex rounded-md bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-indigo-500 disabled:bg-indigo-400"
                  >
                    {isSelecting ? (
                      <div className="flex items-center justify-center gap-2">
                        <LoadingSpinner className="h-5 w-5" />
                        <span>Processing...</span>
                      </div>
                    ) : (
                      'Set Neoface'
                    )}
                  </button>
                </>
            </div>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
};

export default PreviewModal;